import React, { useState, useId, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useMutation as useTanstackMutation } from '@tanstack/react-query';

import { cn } from '@epoch-ui/lib/utils';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { AI_INTEGRATION_V1, OUTLOOK_EDITOR_IMAGES } from 'constants/featureFlags';
import { EVENT_PHOTO_MUTATION } from 'graphql/mutations/file-upload';
import RichTextEditor from 'components/common/inputs/RichTextEditor';
import EventDescriptionPromptWrapper from 'components/common/AIModals/generateAIDescription/EventDescriptionPromptWrapper';
import { EditDescriptionConfirm } from 'components/common/AIModals/editAIDescrption/EditDescriptionConfirm';
import {
  fetchDescription,
  FetchDescriptionResult,
  FetchDescriptionVariables
} from 'components/common/AIModals/editAIDescrption/api/fetchDescription';
import {
  FormField,
  FormControl,
  FormLabel,
  FormItem,
  FormMessage
} from '@epoch-ui/components/Form';

export interface EventDescriptionFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  eventTitle?: string;
  compactToolbar?: boolean;
}

export const EventDescriptionField = React.forwardRef<HTMLDivElement, EventDescriptionFieldProps>(
  (
    {
      name,
      label = 'Description',
      placeholder,
      className,
      disabled,
      eventTitle = '',
      compactToolbar = false
    },
    ref
  ) => {
    const id = useId();
    const formCtx = useFormContext();
    const { showToast } = useToaster();

    const rawDescription = (formCtx.watch(name) || '')
      .replace(/<[^>]*>/g, '')
      .replace(/&[^;]+;/g, '')
      .trim();

    // Feature flags
    const isAIIntegrationEnabled = useFeatureFlag(AI_INTEGRATION_V1);
    const isOutlookEditorImagesEnabled = useFeatureFlag(OUTLOOK_EDITOR_IMAGES);

    // AI Modal states
    const [openDescriptionAIPrompt, setOpenDescriptionAIPrompt] = useState(false);
    const [editAIModalState, setEditAIModalState] = useState<{ open: boolean }>({ open: false });
    const [aiDescription, setAiDescription] = useState('');
    const abortRef = useRef<AbortController | null>(null);

    const { mutate: requestEditDescription, isPending: aiLoading } = useTanstackMutation<
      FetchDescriptionResult,
      Error,
      FetchDescriptionVariables & { signal?: AbortSignal }
    >({
      mutationFn: fetchDescription,
      onSuccess: (data) => {
        if (data?.editDescription.answer) {
          setAiDescription(data.editDescription.answer);
          setEditAIModalState({ open: true });
        }
      },
      onError: (error) => {
        if (error.name === 'AbortError') return;
        setEditAIModalState({ open: false });
        showToast('Description could not be generated. Try again later.', {
          variant: ToasterVariant.ERROR
        });
      }
    });

    // Photo upload mutation
    const [uploadEventPhoto, { loading: uploadingPhoto }] = useMutation(EVENT_PHOTO_MUTATION);

    const uploadImageInEditor = async (file: File): Promise<string> => {
      try {
        const { data } = await uploadEventPhoto({
          variables: { file, isCoverPhoto: false }
        });
        if (data?.uploadEventPhoto?.ok && data?.uploadEventPhoto?.photoUrl) {
          return data.uploadEventPhoto.photoUrl;
        }
        const errorMessage =
          data?.uploadEventPhoto?.errors?.[0]?.message || 'Image upload failed from server';
        showToast(errorMessage, { variant: ToasterVariant.ERROR });
        throw new Error(errorMessage);
      } catch (error) {
        console.error('Editor image upload error:', error);
        const errMessage =
          error instanceof Error ? error.message : 'Unknown error during image upload';
        showToast(`Failed to upload image: ${errMessage}`, {
          variant: ToasterVariant.ERROR
        });
        throw error;
      }
    };

    const handleAIModalOpen = (): void => {
      setOpenDescriptionAIPrompt(true);
    };

    const handleEditDescription = (options: string[], instruction?: string): void => {
      abortRef.current = new AbortController();
      setEditAIModalState({ open: true });
      requestEditDescription({
        origDescription: rawDescription,
        editOptions: options,
        editInstruction: instruction,
        signal: abortRef.current.signal
      });
    };

    return (
      <>
        <FormField
          control={formCtx.control}
          name={name || id}
          render={({ field, fieldState }): React.ReactElement => {
            const isDescriptionEmpty = rawDescription.length === 0;
            const isFieldDisabled = disabled || uploadingPhoto;

            return (
              <FormItem className={cn('tw-mb-0', className)} ref={ref}>
                <FormLabel className="tw-mb-2">{label}</FormLabel>
                <FormControl className="tw-mt-0">
                  <RichTextEditor
                    value={field.value || ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder={placeholder || 'What should guests know about this event?'}
                    disabled={isFieldDisabled}
                    error={!!fieldState.error?.message}
                    displayImages={isOutlookEditorImagesEnabled}
                    uploadImage={uploadImageInEditor}
                    isDescriptionEmpty={isDescriptionEmpty}
                    showAIModal={isAIIntegrationEnabled}
                    handleAIModalOpen={handleAIModalOpen}
                    handleEditDescription={handleEditDescription}
                    compactToolbar={compactToolbar}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        {/* AI Integration Modals */}
        {isAIIntegrationEnabled && (
          <>
            <EventDescriptionPromptWrapper
              shouldBeginStep={openDescriptionAIPrompt}
              eventTitle={eventTitle}
              resetStep={(): void => setOpenDescriptionAIPrompt(false)}
              submitDescription={(description: string): void => {
                formCtx.setValue(name, description, {
                  shouldValidate: true,
                  shouldDirty: true
                });
                setOpenDescriptionAIPrompt(false);
              }}
              showToastError={(): void => {
                setOpenDescriptionAIPrompt(false);
                showToast('Description could not be generated. Try again later.', {
                  variant: ToasterVariant.ERROR
                });
              }}
            />
            {editAIModalState.open && (
              <EditDescriptionConfirm
                open={editAIModalState.open}
                onOpenChange={(open): void => {
                  if (!open) abortRef.current?.abort();
                  setEditAIModalState({ open });
                }}
                replaceDescription={(description: string): void => {
                  formCtx.setValue(name, description, {
                    shouldValidate: true,
                    shouldDirty: true
                  });
                  setEditAIModalState({ open: false });
                }}
                appendDescription={(description: string): void => {
                  const currentDesc = formCtx.getValues(name) || '';
                  const newDescription = currentDesc
                    ? `${currentDesc}<p><br></p>${description}`
                    : description;
                  formCtx.setValue(name, newDescription, {
                    shouldValidate: true,
                    shouldDirty: true
                  });
                  setEditAIModalState({ open: false });
                }}
                description={aiDescription}
                loading={aiLoading}
              />
            )}
          </>
        )}
      </>
    );
  }
);

EventDescriptionField.displayName = 'EventDescriptionField';
